<template>
  <v-row>
    <v-col cols="12">
      <v-card>
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">Feed</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn :loading="$store.state.agents.retrieveLoader" @click="getList" icon small><v-icon small>mdi-refresh</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />

        <!-- filters -->
        <v-row class="px-4 py-3" align="center" no-gutters>
          <v-select v-model="agentFilter" :items="agentItems" @change="applyFilters" label="Agente" dense outlined hide-details clearable class="mr-3" style="max-width: 220px;" />
          <v-select v-model="severityFilter" :items="severityItems" @change="applyFilters" label="Severidad" dense outlined hide-details clearable class="mr-3" style="max-width: 200px;" />
          <v-select v-model="sourceFilter" :items="sourceItems" @change="applyFilters" label="Tipo" dense outlined hide-details clearable style="max-width: 200px;" />
        </v-row>
        <!-- end filters -->

        <v-divider />

        <v-card-text class="pa-0 background" style="height: calc(100vh - 240px); overflow-y: auto;">
          <v-skeleton-loader v-if="$store.state.agents.retrieveLoader" class="pa-4" type="list-item-three-line, list-item-three-line, list-item-three-line" />
          <template v-else>
            <!-- empty state -->
            <div v-if="!inboxList.length" class="text-center py-12">
              <v-icon size="56" color="grey lighten-1">mdi-inbox-outline</v-icon>
              <div class="mt-3 subtitle-1 font-weight-medium text--secondary">Feed al día</div>
              <div class="body-2 text--disabled mt-1">No hay aprobaciones ni insights pendientes de atención.</div>
            </div>

            <template v-else>
              <v-card v-for="item in inboxList" :key="`${item.source}-${item.id}`" class="mx-4 my-3 agent-inbox-item" outlined>
                <v-list-item class="py-2" :class="{ 'agent-inbox-clickable': item.source === 'insight' }" @click="item.source === 'insight' ? openInsight(item) : null">
                  <v-list-item-avatar class="my-0 mr-3 align-self-center outlined" size="40">
                    <img :alt="agentName(item.agent)" :src="agentAvatarSrc(item.agent)" />
                  </v-list-item-avatar>

                  <v-list-item-content class="py-1">
                    <div class="d-flex align-center mb-1">
                      <v-chip class="mr-2" :color="severityStyle(item.severity).color" :text-color="severityStyle(item.severity).textColor" label small>
                        <v-icon left small>{{ severityStyle(item.severity).icon }}</v-icon>{{ severityStyle(item.severity).label }}
                      </v-chip>
                      <v-chip class="mr-2" color="defaultGrey" label small text-color="secondary">
                        {{ sourceLabel(item.source) }}
                      </v-chip>
                      <span v-if="item.due_at" class="mx-1 caption" :class="isOverdue(item) ? 'red--text' : 'text--disabled'">
                        <v-icon class="mr-1" size="14" :color="isOverdue(item) ? 'red' : 'secondary'">mdi-clock-outline</v-icon>{{ item.due_at | dateTime }}
                      </span>
                    </div>
                    <span class="body-2 text--secondary font-weight-medium">
                      <span class="text--primary">{{ agentName(item.agent) }}:</span> {{ item.title }}
                    </span>
                    <span class="body-2 text--primary" style="white-space: normal;">{{ item.summary }}</span>
                    <span v-if="item.entity || item.entity_type" class="caption text--disabled mt-1">
                      <entity-label :entity="item.entity" :entity-type="item.entity_type" :entity-id="item.entity_id" compact />
                    </span>
                  </v-list-item-content>

                  <v-list-item-action class="flex-row align-center" @click.stop>
                    <template v-if="item.source === 'approval'">
                      <v-btn class="mr-2" @click="resolveApproval(item, 'rejected')" color="secondary" :loading="actionLoader === `${item.id}_rejected`" :disabled="!!actionLoader" outlined small>Rechazar</v-btn>
                      <v-btn @click="resolveApproval(item, 'approved')" color="primary" depressed :loading="actionLoader === `${item.id}_approved`" :disabled="!!actionLoader" small><v-icon left x-small>mdi-check</v-icon>Aprobar</v-btn>
                      <v-btn class="ml-2" :to="{ name: 'AgentRetrieve', params: { id: item.agent } }" color="secondary" icon small><v-icon small>mdi-open-in-new</v-icon></v-btn>
                    </template>
                    <template v-else>
                      <v-btn class="mr-2" @click="acknowledge(item, 'dismissed')" color="secondary" :loading="actionLoader === `${item.id}_dismissed`" :disabled="!!actionLoader" outlined small>Descartar</v-btn>
                      <v-btn @click="acknowledge(item, 'acknowledged')" color="primary" depressed :loading="actionLoader === `${item.id}_acknowledged`" :disabled="!!actionLoader" small><v-icon left x-small>mdi-check</v-icon>Reconocer</v-btn>
                    </template>
                  </v-list-item-action>
                </v-list-item>
              </v-card>

              <!-- pagination -->
              <div v-if="totalPages > 1" class="text-center py-4">
                <v-pagination v-model="page" :length="totalPages" total-visible="7" />
              </div>
            </template>
          </template>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- insight detail drawer -->
    <v-navigation-drawer class="elevation-8" v-model="drawerInsight" fixed hide-overlay right temporary style="height: calc(100vh - 64px); top: 64px;" width="640">
      <v-card v-if="selectedInsight">
        <v-toolbar flat height="56">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">{{ selectedInsight.title }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items v-if="selectedInsight.status === 'new'">
            <v-row align="center" justify="center" class="mr-1">
              <v-btn class="mr-2 align-self-center" @click="acknowledgeSelected('dismissed')" color="secondary" :disabled="ackLoader === 'acknowledged'" :loading="ackLoader === 'dismissed'" outlined>Descartar</v-btn>
              <v-btn class="align-self-center" @click="acknowledgeSelected('acknowledged')" color="primary" depressed :disabled="ackLoader === 'dismissed'" :loading="ackLoader === 'acknowledged'"><v-icon left small>mdi-check</v-icon>Reconocer</v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="background overflow-y-auto pa-4" style="height: calc(100vh - 120px)">
          <agent-insight-body :insight="selectedInsight" />
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import ListViewMixin from '@/mixins/ListViewMixin'
import AgentInsightBody from '@/components/agents/AgentInsightBody'
import EntityLabel from '@/components/agents/EntityLabel'
import { agentInsightSeverity, agentInboxSourceLabel } from '@/collections/agents'

export default {
  components: {
    AgentInsightBody,
    EntityLabel
  },
  mixins: [
    ListViewMixin
  ],
  data () {
    return {
      agentFilter: null,
      severityFilter: null,
      sourceFilter: null,
      actionLoader: null,
      drawerInsight: false,
      selectedInsight: null,
      ackLoader: null,
      severityItems: [
        { text: 'Informativo', value: 'info' },
        { text: 'Atención', value: 'warning' },
        { text: 'Crítico', value: 'critical' }
      ],
      sourceItems: [
        { text: 'Para aprobación', value: 'approval' },
        { text: 'Insight', value: 'insight' }
      ]
    }
  },
  computed: {
    ...mapState({
      inboxList: state => state.agents.inboxList,
      inboxCount: state => state.agents.inboxCount,
      agentsList: state => state.agents.agentsList
    }),
    count () {
      return this.inboxCount
    },
    agentItems () {
      return this.agentsList.map(a => ({ text: a.name, value: a.id }))
    }
  },
  created () {
    if (!this.agentsList.length) {
      this.$store.dispatch('agents/LIST', { resource: 'agents', loader: false })
    }
    this.agentFilter = this.$route.query.agent || null
    this.severityFilter = this.$route.query.severity || null
    this.sourceFilter = this.$route.query.source || null
  },
  methods: {
    getList () {
      this.$store.dispatch('agents/LIST', {
        resource: 'agents/inbox',
        query: { ...this.$route.query, expand: 'entity' }
      })
    },
    applyFilters () {
      const query = {}
      if (this.agentFilter) query.agent = this.agentFilter
      if (this.severityFilter) query.severity = this.severityFilter
      if (this.sourceFilter) query.source = this.sourceFilter
      this.query = query
      this.setQuery()
    },
    severityStyle (severity) {
      return agentInsightSeverity[severity] || agentInsightSeverity.info
    },
    sourceLabel (source) {
      return agentInboxSourceLabel[source] || source
    },
    agentName (id) {
      const agent = this.agentsList.find(a => a.id === id)
      return agent ? agent.name : 'Agente'
    },
    agentAvatarSrc (id) {
      const agent = this.agentsList.find(a => a.id === id)
      const avatar = (agent && agent.avatar) || 'avatar-1'
      return require(`@/assets/appAsset-agents-${avatar}.jpg`)
    },
    isOverdue (item) {
      return item.due_at && new Date(item.due_at).getTime() < Date.now()
    },
    resolveApproval (item, status) {
      this.actionLoader = `${item.id}_${status}`
      this.$store.dispatch('agents/CREATE', {
        resource: 'agents/approvals',
        detail: 'resolve',
        id: item.id,
        payload: { status },
        commitInstance: false
      })
      .then(() => {
        this.$dialog.message.success(status === 'approved' ? 'Aprobado' : 'Rechazado')
        this.getList()
      })
      .catch((error) => {
        const msg = error.response && error.response.data ? this.parseErrors(error.response.data) : null
        this.$dialog.message.error(msg || 'No se pudo procesar la aprobación')
      })
      .finally(() => {
        this.actionLoader = null
      })
    },
    acknowledge (item, status) {
      this.actionLoader = `${item.id}_${status}`
      this.$store.dispatch('agents/CREATE', {
        resource: 'agents/insights',
        detail: 'acknowledge',
        id: item.id,
        payload: { status },
        commitInstance: false
      })
      .then(() => {
        this.$dialog.message.success(status === 'acknowledged' ? 'Insight reconocido' : 'Insight descartado')
        this.getList()
      })
      .catch((error) => {
        const msg = error.response && error.response.data ? this.parseErrors(error.response.data) : null
        this.$dialog.message.error(msg || 'No se pudo procesar el insight')
      })
      .finally(() => {
        this.actionLoader = null
      })
    },
    openInsight (item) {
      this.$store.dispatch('agents/RETRIEVE', {
        resource: 'agents/insights',
        id: item.id,
        query: { expand: 'entity' },
        setDetail: false,
        loader: false
      })
      .then((response) => {
        this.selectedInsight = response.data
        this.drawerInsight = true
      })
      .catch((error) => {
        const msg = error.response && error.response.data ? this.parseErrors(error.response.data) : null
        this.$dialog.message.error(msg || 'No se pudo cargar el insight')
      })
    },
    acknowledgeSelected (status) {
      if (!this.selectedInsight) return
      this.ackLoader = status
      this.$store.dispatch('agents/CREATE', {
        resource: 'agents/insights',
        detail: 'acknowledge',
        id: this.selectedInsight.id,
        payload: { status },
        commitInstance: false
      })
      .then(() => {
        this.$dialog.message.success(status === 'acknowledged' ? 'Insight reconocido' : 'Insight descartado')
        this.drawerInsight = false
        this.selectedInsight = null
        this.getList()
      })
      .catch((error) => {
        const msg = error.response && error.response.data ? this.parseErrors(error.response.data) : null
        this.$dialog.message.error(msg || 'No se pudo procesar el insight')
      })
      .finally(() => {
        this.ackLoader = null
      })
    }
  }
}
</script>
<style scoped>
.agent-inbox-clickable {
  cursor: pointer;
}
</style>
